import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { all } from "../Redux/actions/index";
import packageJson from "../../../package.json";

function Menu() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatcher = useDispatch();
  const [profileInfo, setProfileInfo] = useState({});
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Optimized logout function
  const handleLogout = useCallback(() => {
    sessionStorage.removeItem("TES_Admin_ID");
    toast.success("Logout Successfully...!");
    navigate("/");
  }, [navigate]);

  // Get active class based on current path
  const getActiveClass = (path) => {
    return location.pathname.match(path) ? "active" : "";
  };

  // Active class calculations
  const dashboardClass = getActiveClass(/^\/dashboard|^\/detailed-dashboard/);
  const companyClass = getActiveClass(/^\/company/);
  const pricingConfigClass = getActiveClass(/^\/pricing-config/);
  const billingDriftClass = getActiveClass(/^\/billing-drift/);
  const paygDriftClass = getActiveClass(/^\/payg-drift/);
  const paygInvoicesClass = getActiveClass(/^\/payg-invoices/);
  const usersClass = getActiveClass(/^\/users$/);
  const usersLogClass = getActiveClass(/^\/users-log/);

  // Profile data fetching function - removed from useCallback to prevent infinite loops
  const getProfileData = async () => {
    if (profileLoaded) return; // Prevent multiple calls

    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/get-profile`;
      const bodyFormData = new URLSearchParams();
      bodyFormData.append("auth_code", process.env.REACT_APP_AUTH_CODE);
      bodyFormData.append("id", sessionStorage.getItem("TES_Admin_ID"));

      const response = await axios({
        method: "post",
        url: myurl,
        data: bodyFormData,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (response.data.success === true) {
        setProfileInfo(response.data.data);
        setProfileLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfileLoaded(true); // Set to true even on error to prevent retries
    }
  };

  // Authentication check and profile data fetch - simplified useEffect
  useEffect(() => {
    if (sessionStorage.getItem("TES_Admin_ID") == null) {
      toast.error("Please login first...!");
      navigate("/");
      return;
    }

    // Only call getProfileData if not already loaded
    if (!profileLoaded) {
      getProfileData();
    }
  }, []); // Empty dependency array to run only once

  const handleNavClick = useCallback(() => {
    dispatcher(all());
  }, [dispatcher]);

  return (
    <>
      {/* Header */}
      <div id="header" className="header navbar-default">
        <div className="navbar-header">
          <Link to="/dashboard" className="navbar-brand">
            <span className="navbar-logo"></span>
            <b className="mr-1">SpatialVision </b> Admin
          </Link>
          <button
            type="button"
            className="navbar-toggle"
            data-click="sidebar-toggled"
          >
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </button>
        </div>

        <ul className="navbar-nav navbar-right">
          <li className="dropdown navbar-user">
            <a className="dropdown-toggle" data-toggle="dropdown">
              <img
                src={
                  profileInfo.profile_pic
                    ? profileInfo.profile_pic
                    : "assets/img/default-user.png"
                }
                alt="Profile"
                onError={(e) => {
                  e.target.src = "assets/img/default-user.png";
                }}
              />
              <span className="d-none d-md-inline">
                {profileInfo.username || "Admin"}
              </span>
              <b className="caret"></b>
            </a>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to="/profile" className="dropdown-item">
                Edit Profile
              </Link>
              <button
                onClick={handleLogout}
                className="dropdown-item"
                type="button"
              >
                Log Out
              </button>
              <div className="dropdown-divider"></div>
              <span
                className="dropdown-item-text"
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  cursor: "default",
                }}
              >
                v{packageJson?.version}
              </span>
            </div>
          </li>
        </ul>
      </div>

      {/* Sidebar */}
      <div id="sidebar" className="sidebar">
        <div data-scrollbar="true" data-height="100%">
          <ul className="nav">
            <li className="nav-header">Navigation</li>

            {/* Dashboard */}
            <li className={dashboardClass} onClick={handleNavClick}>
              <Link to="/dashboard">
                <i className="fa fa-th-large"></i>
                <span>Dashboard</span>
              </Link>
            </li>

            {/* Company */}
            <li className={companyClass} onClick={handleNavClick}>
              <Link to="/company">
                <i className="fa fa-sitemap"></i>
                <span>Company</span>
              </Link>
            </li>

            {/* Pricing Configuration - NEW */}
            <li className={pricingConfigClass} onClick={handleNavClick}>
              <Link to="/pricing-config">
                <i className="fa fa-dollar-sign"></i>
                <span>Pricing Config</span>
              </Link>
            </li>

            {/* Billing Drift - companies charged for less (or more) than they use */}
            <li className={billingDriftClass} onClick={handleNavClick}>
              <Link to="/billing-drift">
                <i className="fa fa-balance-scale"></i>
                <span>Billing Drift</span>
              </Link>
            </li>

            {/* Pay As You Go Drift - topos that never turned into money */}
            <li className={paygDriftClass} onClick={handleNavClick}>
              <Link to="/payg-drift">
                <i className="fa fa-bolt"></i>
                <span>Pay As You Go Drift</span>
              </Link>
            </li>

            {/* Pay As You Go Invoices - what the monthly job did, plus manual generate / collect */}
            <li className={paygInvoicesClass} onClick={handleNavClick}>
              <Link to="/payg-invoices">
                <i className="fa fa-file-invoice-dollar"></i>
                <span>Pay As You Go Invoices</span>
              </Link>
            </li>

            {/* Users */}
            <li className={usersClass} onClick={handleNavClick}>
              <Link to="/users">
                <i className="fa fa-user"></i>
                <span>Users</span>
              </Link>
            </li>

            {/* Users Log */}
            <li className={usersLogClass} onClick={handleNavClick}>
              <Link to="/users-log">
                <i className="fa fa-users"></i>
                <span>Users Log</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="sidebar-bg"></div>
    </>
  );
}

export default Menu;
